import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import {
  useAssignableProfilesForInspections,
  type AssignableProfile,
} from "./useAssignableProfiles";
import { dialogDataKeys } from "./queryKeys";

// Shared types for dialog data
interface DialogChecklist {
  id: string;
  name: string;
  description?: string | null;
  version: string | null;
}

interface DialogPosition {
  id: string;
  name: string;
}

/**
 * Fetches published and active checklists for dialog selection.
 * Uses longer staleTime (5 minutes) as checklists change infrequently.
 */
function useDialogChecklists(enabled: boolean = true, teamId?: string | null) {
  const queryClient = useQueryClient();
  const queryKey = [...dialogDataKeys.checklists, teamId ?? "all"];
  useRealtimeSubscription(
    "checklists",
    () => queryClient.invalidateQueries({ queryKey: dialogDataKeys.checklists }),
    { enabled },
  );

  return useQuery({
    queryKey,
    queryFn: async (): Promise<DialogChecklist[]> => {
      const { data, error } = await supabase
        .from("checklists")
        .select("id, name, description, version, checklist_teams(team_id)")
        .eq("is_active", true)
        .eq("status", "published")
        .order("name");

      if (error) throw error;
      if (!data) return [];

      // Filter: global checklists (no team assignments) OR assigned to active team
      if (!teamId) return data.map(({ checklist_teams, ...rest }) => rest);

      return data
        .filter((c) => {
          const teams = (c.checklist_teams ?? []) as { team_id: string }[];
          return teams.length === 0 || teams.some((t) => t.team_id === teamId);
        })
        .map(({ checklist_teams, ...rest }) => rest);
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetches inspection positions for dialog selection.
 * Uses longer staleTime (5 minutes) as positions change infrequently.
 */
function useDialogPositions(enabled: boolean = true) {
  const queryClient = useQueryClient();
  useRealtimeSubscription(
    "inspection_positions",
    () => queryClient.invalidateQueries({ queryKey: dialogDataKeys.positions }),
    { enabled },
  );

  return useQuery({
    queryKey: dialogDataKeys.positions,
    queryFn: async (): Promise<DialogPosition[]> => {
      const { data, error } = await supabase
        .from("inspection_positions")
        .select("id, name")
        .order("name");

      if (error) throw error;
      return data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Combined hook for dialog data with options for what to load.
 * Provides all data needed for inspection/schedule dialogs.
 */
export function useDialogData(
  options: {
    loadChecklists?: boolean;
    loadPositions?: boolean;
    loadProfiles?: boolean;
    enabled?: boolean;
    teamId?: string | null;
  } = {},
) {
  const {
    loadChecklists = true,
    loadPositions = true,
    loadProfiles = true,
    enabled = true,
    teamId,
  } = options;

  const checklistsQuery = useDialogChecklists(enabled && loadChecklists, teamId);
  const positionsQuery = useDialogPositions(enabled && loadPositions);
  const profilesQuery = useAssignableProfilesForInspections(teamId, enabled && loadProfiles);

  // Only consider profiles loading if we're actually loading profiles
  const isLoading =
    (loadChecklists && checklistsQuery.isLoading) ||
    (loadPositions && positionsQuery.isLoading) ||
    (loadProfiles && profilesQuery.isLoading);

  return {
    checklists: checklistsQuery.data ?? [],
    positions: positionsQuery.data ?? [],
    profiles: loadProfiles ? (profilesQuery.data ?? []) : [],
    isLoading,
    isChecklistsLoading: checklistsQuery.isLoading,
    isPositionsLoading: positionsQuery.isLoading,
    isProfilesLoading: profilesQuery.isLoading,
  };
}

// Re-export for convenience
export type { AssignableProfile };
